import React, { useEffect, useRef, useState } from "react";
import {
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
} from "@mui/material";
import { CameraAlt as CameraIcon, Close as CloseIcon, Search } from "@mui/icons-material";

const DEBOUNCE_MS = 300;

interface ProductSearchBoxProps {
    /** The search currently applied to the list. Setting it from outside
     * (a barcode scan, Clear All) updates the box. */
    value: string;
    /** Called with the trimmed term once typing pauses, on Enter, or on clear. */
    onSearch: (term: string) => void;
    /** A search is in flight — shown as a small spinner, never by disabling. */
    searching?: boolean;
    placeholder: string;
    onScanClick?: () => void;
    autoFocus?: boolean;
}

/**
 * The Products page search box. What's being typed lives here, so each
 * keystroke re-renders only this box — not the page and its 50-row grid,
 * which is what made typing lag. The page hears about the term once typing
 * pauses for a moment.
 */
export const ProductSearchBox: React.FC<ProductSearchBoxProps> = ({
    value,
    onSearch,
    searching = false,
    placeholder,
    onScanClick,
    autoFocus,
}) => {
    const [draft, setDraft] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSent = useRef(value);

    const send = (term: string) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
        const trimmed = term.trim();
        lastSent.current = trimmed;
        onSearch(trimmed);
    };

    // The page changed the search itself (barcode scan, Clear All).
    useEffect(() => {
        if (value !== lastSent.current) {
            lastSent.current = value;
            setDraft(value);
        }
    }, [value]);

    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current);
    }, []);

    useEffect(() => {
        if (!autoFocus) return;
        const t = setTimeout(() => inputRef.current?.focus(), 300);
        return () => clearTimeout(t);
    }, [autoFocus]);

    const handleChange = (next: string) => {
        setDraft(next);
        if (timer.current) clearTimeout(timer.current);
        // Nothing new to look for (e.g. a trailing space) — don't refetch.
        if (next.trim() === lastSent.current) {
            timer.current = null;
            return;
        }
        timer.current = setTimeout(() => send(next), DEBOUNCE_MS);
    };

    const clear = () => {
        setDraft("");
        send("");
        inputRef.current?.focus();
    };

    return (
        <TextField
            inputRef={inputRef}
            placeholder={placeholder}
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    send(draft);
                } else if (e.key === "Escape" && draft) {
                    e.preventDefault();
                    clear();
                }
            }}
            size="small"
            sx={{ minWidth: { xs: 0, sm: 350 }, width: { xs: "100%", sm: "auto" } }}
            inputProps={{ "aria-label": "Search products" }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        {searching ? <CircularProgress size={18} /> : <Search />}
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        {draft && (
                            <IconButton size="small" onClick={clear} aria-label="Clear search">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                        {onScanClick && (
                            <Tooltip title="Scan barcode with camera">
                                <IconButton size="small" onClick={onScanClick} edge="end">
                                    <CameraIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </InputAdornment>
                ),
            }}
        />
    );
};
